import React, { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../services/analytics';
import { isNarrationSupported, pauseNarration, resumeNarration, speak, stopNarration } from '../services/narration';
import '../styles/StoryModeTour.css';

// Roughly 2.5 words/sec at the narration rate used in narration.js — used only to advance the
// "currently highlighted" step in the UI; it doesn't need to be sample-accurate.
const estimateDurationMs = (text) => {
  const wordCount = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(3000, Math.round((wordCount / 2.5) * 1000));
};

const buildScript = (subject, books) => {
  const segments = [];
  const subjectLabel = subject?.label || 'this subject';
  const intro = subject?.description
    ? `Welcome to the ${subjectLabel} shelf. ${subject.description}`
    : `Welcome to the ${subjectLabel} shelf. Let's explore some of what's here.`;
  segments.push({ key: 'intro', title: 'Introduction', text: intro });

  books.slice(0, 5).forEach((book, index) => {
    const text = book.author
      ? `Next up: ${book.title}, by ${book.author}.`
      : `Next up: ${book.title}.`;
    segments.push({ key: `book-${book.biblio_id || index}`, title: book.title, book, text });
  });

  segments.push({ key: 'outro', title: 'That\'s the tour', text: 'That covers this shelf for now. Feel free to keep browsing on your own.' });

  return segments;
};

function StoryModeTour({ subject, books }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timerRef = useRef(null);
  const segmentsRef = useRef([]);

  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    stopNarration();
  }, []);

  const advance = (index) => {
    const segments = segmentsRef.current;
    if (index >= segments.length) {
      setIsPlaying(false);
      setActiveIndex(-1);
      return;
    }

    setActiveIndex(index);
    const segment = segments[index];
    speak(segment.text);

    timerRef.current = setTimeout(() => {
      advance(index + 1);
    }, estimateDurationMs(segment.text));
  };

  const handlePlay = () => {
    if (isPaused) {
      resumeNarration();
      setIsPaused(false);
      const currentSegment = segmentsRef.current[activeIndex];
      if (currentSegment) {
        timerRef.current = setTimeout(() => {
          advance(activeIndex + 1);
        }, estimateDurationMs(currentSegment.text));
      }
      return;
    }

    segmentsRef.current = buildScript(subject, books);
    setIsOpen(true);
    setIsPlaying(true);
    trackEvent('story_mode_started', { subjectKey: subject?.key });
    advance(0);
  };

  const handlePause = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pauseNarration();
    setIsPaused(true);
  };

  const handleStop = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    stopNarration();
    setIsPlaying(false);
    setIsPaused(false);
    setActiveIndex(-1);
  };

  if (!isNarrationSupported() || !books.length) {
    return null;
  }

  return (
    <div className="story-mode-tour">
      <button type="button" className="story-mode-tour__toggle" onClick={isPlaying ? handleStop : handlePlay}>
        {isPlaying ? '⏹ Stop tour' : '🎧 Take a guided tour'}
      </button>

      {isOpen && isPlaying && (
        <div className="story-mode-tour__panel">
          <div className="story-mode-tour__controls">
            {isPaused ? (
              <button type="button" onClick={handlePlay}>▶ Resume</button>
            ) : (
              <button type="button" onClick={handlePause}>⏸ Pause</button>
            )}
            <button type="button" onClick={handleStop}>⏹ Stop</button>
          </div>
          <ul className="story-mode-tour__steps">
            {segmentsRef.current.map((segment, index) => (
              <li
                key={segment.key}
                className={`story-mode-tour__step ${index === activeIndex ? 'story-mode-tour__step--active' : ''}`}
              >
                {segment.title}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default StoryModeTour;
